import * as React from 'react';
import {Component} from 'react';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import {ListProps} from './List';

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

export interface InjectedDraggableProps {
    draggableProps: any;
    dragHandleProps: any;
    innerRef: any;
    isDragged: boolean;
    inDrag: boolean;
    key?: string;
}

interface DraggableListProps<T> {
    renderItem(props: {item: T; index} & InjectedDraggableProps): JSX.Element;
    data: Array<T>;
    componentClasses?: string;
    draggable?: boolean;
    onDragStart?: (params: any) => void;
    onDragUpdate?: (params: any) => void;
    onDragEnd?: (result: any) => void;
}

export default class DraggableList<T> extends Component<
    Pick<ListProps<T>, Exclude<keyof ListProps<T>, 'renderItem'>> & DraggableListProps<T>,
    any
> {
    constructor(props) {
        super(props);

        this.onDragEnd = this.onDragEnd.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
    }

    static defaultProps = {
        draggable: true
    };

    onDragEnd(result) {
        const {data, onDragEnd} = this.props;

        // dropped outside the list
        if (!result.destination) {
            return;
        }
        if (onDragEnd) {
            onDragEnd(result);
        } else {
            // didn't reorder items
            if (result.source.index === result.destination.index) {
                return;
            }
            const items = reorder(data, result.source.index, result.destination.index);

            this.props.handleDNDFinish(items, result.destination.index);
        }
    }

    onDragStart(params) {
        if (this.props.onDragStart) {
            this.props.onDragStart(params);
        }
        this.setState({draggedId: params.draggableId});
    }

    render() {
        const {keyExtractor, data, renderItem, itemProps, componentClasses, onDragUpdate} =
            this.props;
        return (
            <DragDropContext
                onDragEnd={this.onDragEnd}
                onDragStart={this.onDragStart}
                onDragUpdate={onDragUpdate}
            >
                <Droppable droppableId="droppable">
                    {provided => (
                        <div className={componentClasses} ref={provided.innerRef}>
                            {data.map((item, idx) => {
                                const key = keyExtractor ? keyExtractor(item) : idx;
                                return (
                                    <Draggable key={key} draggableId={`item-${key}`} index={idx}>
                                        {(provided, snapshot) => {
                                            return renderItem({
                                                item: item,
                                                index: idx,
                                                isLastIndex: idx === data.length - 1,
                                                key: key,
                                                ...itemProps,
                                                ...provided,
                                                isDragged: snapshot.isDragging,
                                                inDrag: snapshot.isDragging
                                            });
                                        }}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }
}
